/**
 * `manage-clause` — FR-16, FR-17, FR-26, FR-28, NFR-13.
 *
 * One endpoint, a discriminated union of actions. That shape is chosen because
 * every action here shares the same governance: FR-17 restricts master-clause
 * text to reviewers, and WP-09 §9 puts the search-behaviour surfaces behind the
 * same role — *"editing search behaviour is a governance action, not a user
 * preference; it belongs with whoever already owns master clause text."* One
 * endpoint means one role check, in one place, that cannot be forgotten on the
 * fourth action added.
 *
 * Every action writes an audit row (NFR-07, NFR-13). Changing how retrieval
 * behaves is exactly the kind of change that log exists to record — a reviewer
 * asking *"who altered clause retrieval, and when"* gets a straight answer.
 */
import { z } from 'zod';
import { ClauseCodeSchema, ContractKeySchema, OpaqueIdSchema, VersionNoSchema, } from '../primitives.js';
import { ClauseMatchStateSchema, SearchJudgementSchema } from '../vocabulary.js';
import { AuditReceiptSchema } from '../provenance.js';
import { ClauseSearchAliasSchema, ClauseSearchJudgementSchema, ContractClauseSchema, LibraryClauseSchema, } from '../domain/clause.js';
/* -------------------------------------------------------------------------- */
/* Actions                                                                     */
/* -------------------------------------------------------------------------- */
/**
 * The request body *is* the action — there is no envelope. Each member is
 * `.strict()`, so a typo in an action's field name fails validation instead of
 * being silently dropped on a governance-relevant write.
 */
export const ManageClauseRequestSchema = z.discriminatedUnion('action', [
    /* --- Clause library, FR-16 / FR-17 ------------------------------------- */
    z
        .object({
        action: z.literal('create_clause'),
        clause_code: ClauseCodeSchema,
        title: z.string().min(1).max(200),
        category: z.string().min(1).max(60).nullable(),
        master_text: z.string().min(1),
    })
        .strict(),
    z
        .object({
        /**
         * FR-17. An edit **creates a new version**; it never mutates the text in
         * place.
         *
         * There is a governance consequence, and it is not optional: a judgement
         * recorded against clause v2 is weak evidence about v4, so its weight drops
         * to 0.3 and it queues for re-confirmation (§4.3 loop 3). The response
         * reports how many judgements this edit made stale, so the reviewer sees the
         * cost of the edit at the moment they make it.
         */
        action: z.literal('revise_clause_text'),
        clause_code: ClauseCodeSchema,
        /** The version being edited. A mismatch is a `conflict`, not a silent overwrite. */
        expected_version_no: VersionNoSchema,
        master_text: z.string().min(1),
        title: z.string().min(1).max(200).optional(),
        category: z.string().min(1).max(60).nullable().optional(),
        /** Recorded on the audit row, so the *why* survives with the *what*. */
        revision_note: z.string().min(1).max(1000),
    })
        .strict(),
    z
        .object({
        action: z.literal('set_clause_active'),
        clause_code: ClauseCodeSchema,
        is_active: z.boolean(),
    })
        .strict(),
    z
        .object({
        /** WP-09 manual authority. Positive pins a clause up, negative buries it. */
        action: z.literal('set_clause_boost'),
        clause_code: ClauseCodeSchema,
        boost_weight: z.number().int().min(-100).max(100),
        reason: z.string().min(1).max(500),
    })
        .strict(),
    /* --- Match review, FR-28 ----------------------------------------------- */
    z
        .object({
        /**
         * Resolve a `MEDIUM`-band match from the review queue, or correct a match the
         * matcher got wrong (§4.3 loop 2 — the most valuable of the three feedback
         * loops, because it produces labelled `(paragraph, clause, verdict)` triples
         * as a by-product of normal review work).
         *
         * `clause_code: null` records the paragraph as **bespoke** — a candidate new
         * library clause.
         */
        action: z.literal('resolve_clause_match'),
        contract_clause_id: OpaqueIdSchema,
        clause_code: ClauseCodeSchema.nullable(),
        match_state: ClauseMatchStateSchema.nullable(),
        deviation_note: z.string().min(1).max(2000).nullable(),
    })
        .strict(),
    /* --- Search behaviour, FR-26 / NFR-13 ---------------------------------- */
    z
        .object({
        /**
         * Record a relevance judgement (FR-26).
         *
         * It generalises: judged queries are themselves embedded, so a correction on
         * *"liability cap"* also helps someone who later types *"limit what we owe"*
         * (WP-09 §6). `query_text` is the query as the user typed it, **before** alias
         * expansion — that is what gets embedded.
         */
        action: z.literal('record_search_judgement'),
        query_text: z.string().min(1).max(400),
        clause_code: ClauseCodeSchema,
        judgement: SearchJudgementSchema,
        /** From the search response, so the judgement joins to the logged query. */
        search_id: z.string().min(1).max(64).optional(),
    })
        .strict(),
    z
        .object({
        /** Instantly reversible: delete it and the effect is gone on the next query. */
        action: z.literal('retract_search_judgement'),
        feedback_id: OpaqueIdSchema,
    })
        .strict(),
    z
        .object({
        action: z.literal('create_search_alias'),
        alias_term: z.string().min(1).max(120),
        canonical_term: z.string().min(1).max(200),
    })
        .strict(),
    z
        .object({
        /**
         * Approve a **mined** alias proposal — WP-09 §6 loop 2. The system detects the
         * vocabulary gap and proposes; a human approves. Never automatic: an alias
         * added silently by mining would change retrieval for every future query with
         * nobody accountable for it.
         */
        action: z.literal('approve_search_alias'),
        alias_id: OpaqueIdSchema,
    })
        .strict(),
    z
        .object({
        action: z.literal('set_search_alias_active'),
        alias_id: OpaqueIdSchema,
        is_active: z.boolean(),
    })
        .strict(),
]);
/** The discriminator values, for exhaustiveness checks in handlers and reducers. */
export const MANAGE_CLAUSE_ACTIONS = [
    'create_clause',
    'revise_clause_text',
    'set_clause_active',
    'set_clause_boost',
    'resolve_clause_match',
    'record_search_judgement',
    'retract_search_judgement',
    'create_search_alias',
    'approve_search_alias',
    'set_search_alias_active',
];
/* -------------------------------------------------------------------------- */
/* Response                                                                    */
/* -------------------------------------------------------------------------- */
/**
 * What the action changed. Each field is `null` unless the action touched it, so
 * one response type serves every action without a second discriminated union the
 * front end would have to narrow twice.
 */
export const ManageClauseResponseSchema = z.object({
    clause: LibraryClauseSchema.nullable(),
    contract_clause: ContractClauseSchema.nullable(),
    judgement: ClauseSearchJudgementSchema.nullable(),
    alias: ClauseSearchAliasSchema.nullable(),
    /**
     * How many recorded judgements this action made stale (§4.3 loop 3). Non-zero
     * only for `revise_clause_text`. Surfaced so the reviewer sees the governance
     * cost at the moment of the edit rather than in a report later.
     */
    judgements_marked_stale: z.number().int().min(0),
    /**
     * Contracts whose clause matches are now worth re-checking against the revised
     * text — scoped to what the caller may see. Advisory: re-matching is queued
     * separately, never performed inline.
     */
    affected_contract_keys: z.array(ContractKeySchema),
    audit: AuditReceiptSchema,
});
/* -------------------------------------------------------------------------- */
/* Review queues — the read side                                               */
/* -------------------------------------------------------------------------- */
/** Which admin queue to read. WP-09 §9 and §4.2 name these surfaces. */
export const CLAUSE_REVIEW_QUEUES = [
    /** `MEDIUM` band — the matcher could not separate the candidates (§4.2). */
    'pending_match_review',
    /** `LOW` band — bespoke paragraphs, candidate new library clauses. */
    'bespoke_candidates',
    /** Mined alias proposals awaiting approval. */
    'suggested_aliases',
    /** Judgements recorded against a superseded clause version. */
    'stale_judgements',
];
export const ClauseReviewQueueSchema = z.enum(CLAUSE_REVIEW_QUEUES);
//# sourceMappingURL=manage-clause.js.map